/** CSV-обвязка дашборда: разделитель `;`, кавычки с удвоением, BOM, UTF-8 —
 * порт csvEscape/parseCsvLine/downloadTextFile (build-скрипт, стр. 5791-5822).
 * Форматы файлов идентичны оригиналу (совместимость с уже накопленными файлами),
 * кроме зафиксированного бага: шаблон справочника берёт ЗАГОЛОВОК экспорта. */
import type { AttrsBulkRow } from "@/api/orders-planning";

export function csvEscape(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

/** Порт parseCsvLine: `;`-разделитель, кавычки, `""` как экранирование. */
export function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ";") {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
}

export function downloadTextFile(filename: string, text: string, mime = "text/csv;charset=utf-8"): void {
  const blob = new Blob([`\uFEFF${text}`], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function csvNum(value: string): number {
  const n = Number(String(value).replace(",", ".").trim());
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
}

// ── Справочник артикулов (article-groups-*.csv) ──

export const ATTRS_CSV_HEADER =
  "article;name;product_type;characteristic;volume_ml;merge_product;semi_finished";

export interface AttrsCsvSource {
  article: string;
  name: string;
  product_type: string;
  characteristic: string;
  volume_ml: number | null;
  merge_product: string;
  semi_finished: string;
}

/** Шаблон/экспорт справочника; в экспорте пропускаются полностью пустые строки. */
export function buildAttrsCsv(rows: AttrsCsvSource[], onlyFilled: boolean): string {
  const lines = [ATTRS_CSV_HEADER];
  for (const r of rows) {
    const filled =
      r.product_type || r.characteristic || r.volume_ml || r.merge_product || r.semi_finished;
    if (onlyFilled && !filled) continue;
    lines.push(
      [
        csvEscape(r.article),
        csvEscape(r.name),
        csvEscape(r.product_type),
        csvEscape(r.characteristic),
        csvEscape(r.volume_ml || ""),
        csvEscape(r.merge_product),
        csvEscape(r.semi_finished),
      ].join(";"),
    );
  }
  return lines.join("\r\n");
}

/** Импорт справочника: колонки по индексам 0=article, 2..6 (порт importArticleAttrsCsv). */
export function parseAttrsCsv(text: string): AttrsBulkRow[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  const rows: AttrsBulkRow[] = [];
  for (const line of lines.slice(1)) {
    if (!line.trim()) continue;
    const cols = parseCsvLine(line);
    const article = (cols[0] ?? "").trim();
    if (!article) continue;
    const volume = csvNum(cols[4] ?? "");
    rows.push({
      article,
      product_type: (cols[2] ?? "").trim(),
      characteristic: (cols[3] ?? "").trim(),
      volume_ml: volume || null,
      merge_product: (cols[5] ?? "").trim(),
      semi_finished: (cols[6] ?? "").trim(),
    });
  }
  return rows;
}

// ── Выгрузки артикулов (порт downloadCsv, стр. 8612) ──

export interface ArticleCsvRow {
  article: string;
  name: string;
  group: string;
  characteristic: string;
  abcxyz: string;
  qty: number;
  current: number;
  previous: number;
  last30: number;
  stock_mp: number;
  stock_1c: number;
  stock_total: number;
  last_sale: string;
  mps: Record<string, number>;
}

export function buildArticlesCsv(rows: ArticleCsvRow[]): string {
  const lines = [
    "article;name;group;characteristic;abcxyz;qty;current_month;previous_month;last30;stock_mp;stock_1c;stock_total;last_sale",
  ];
  for (const r of rows) {
    lines.push(
      [
        r.article, r.name, r.group, r.characteristic, r.abcxyz, r.qty, r.current, r.previous,
        r.last30, r.stock_mp, r.stock_1c, r.stock_total, r.last_sale,
      ].map(csvEscape).join(";"),
    );
  }
  return lines.join("\r\n");
}

const DETAIL_MPS: [string, string][] = [["ozon", "Ozon"], ["wb", "Wildberries"], ["ym", "Yandex Market"]];

/** «CSV детали»: 3 строки на артикул (по МП), остатки — общие по артикулу. */
export function buildDetailsCsv(rows: ArticleCsvRow[]): string {
  const lines = [
    "article;name;group;characteristic;marketplace;qty;stock_mp;stock_1c;stock_total;last_sale",
  ];
  for (const r of rows) {
    for (const [code, label] of DETAIL_MPS) {
      lines.push(
        [
          r.article, r.name, r.group, r.characteristic, label, r.mps[code] ?? 0,
          r.stock_mp, r.stock_1c, r.stock_total, r.last_sale,
        ].map(csvEscape).join(";"),
      );
    }
  }
  return lines.join("\r\n");
}

// ── Прогноз продаж (sales-plan-*.csv) ──

const MONTH_SHORT = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

export function planCsvHeader(year: number): string {
  const months = Array.from({ length: 12 }, (_, i) => `${MONTH_SHORT[i]} ${year}`);
  return ["article", "name", "marketplace_code", "marketplace", ...months].join(";");
}

export interface PlanCsvRow {
  article: string;
  name: string;
  channel_code: string;
  channel_name: string;
  values: number[]; // 12 значений, индекс 0 = январь
}

/** Шаблон/экспорт плана (порт downloadPlanTemplate/exportSavedPlan):
 * onlyFilled=true пропускает строки, где все 12 значений нулевые; пусто вместо 0. */
export function buildPlanCsv(year: number, rows: PlanCsvRow[], onlyFilled: boolean): string {
  const lines = [planCsvHeader(year)];
  for (const r of rows) {
    if (onlyFilled && !r.values.some((v) => v > 0)) continue;
    lines.push(
      [
        csvEscape(r.article),
        csvEscape(r.name),
        csvEscape(r.channel_code),
        csvEscape(r.channel_name),
        ...r.values.map((v) => csvEscape(v || "")),
      ].join(";"),
    );
  }
  return lines.join("\r\n");
}

export interface PlanCsvEntry {
  article: string;
  channel_code: string;
  months: Record<number, number>; // 1..12, все 12 (0 ⇒ удаление)
}

/** Импорт плана (порт importPlanCsv): год берётся из UI; строка игнорируется,
 * если артикул пуст или канал не входит в допустимые. Пишутся ВСЕ 12 месяцев. */
export function parsePlanCsv(text: string, validChannels: Set<string>): PlanCsvEntry[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  const entries: PlanCsvEntry[] = [];
  for (const line of lines.slice(1)) {
    if (!line.trim()) continue;
    const cols = parseCsvLine(line);
    const article = (cols[0] ?? "").trim();
    const channel = (cols[2] ?? "").trim().toLowerCase();
    if (!article || !validChannels.has(channel)) continue;
    const months: Record<number, number> = {};
    for (let i = 0; i < 12; i += 1) {
      months[i + 1] = csvNum(cols[4 + i] ?? "");
    }
    entries.push({ article, channel_code: channel, months });
  }
  return entries;
}

// ── План производства (production-*.csv) ──

export interface ProductionCsvWeek {
  key: string; // "2026-W12"
  month: string;
}

/** Шапка производства (порт productionCsvHeader): недели месяца + comment:YYYY-MM. */
export function productionCsvHeader(weeks: ProductionCsvWeek[]): string {
  const cols = ["article", "name", "group", "characteristic", "volume_ml"];
  let currentMonth = "";
  for (const w of weeks) {
    if (currentMonth && w.month !== currentMonth) cols.push(`comment:${currentMonth}`);
    cols.push(w.key);
    currentMonth = w.month;
  }
  if (currentMonth) cols.push(`comment:${currentMonth}`);
  return cols.join(";");
}

export interface ProductionCsvRow {
  article: string;
  name: string;
  group: string;
  characteristic: string;
  volume_ml: number | null;
  weeks: Record<string, number>; // displayValue по неделям
  comments: Record<string, string>; // month → text
}

export function buildProductionCsv(weeks: ProductionCsvWeek[], rows: ProductionCsvRow[]): string {
  const header = productionCsvHeader(weeks);
  const cols = header.split(";");
  const lines = [header];
  for (const r of rows) {
    const cells = cols.map((col, i) => {
      if (i === 0) return csvEscape(r.article);
      if (i === 1) return csvEscape(r.name);
      if (i === 2) return csvEscape(r.group);
      if (i === 3) return csvEscape(r.characteristic);
      if (i === 4) return csvEscape(r.volume_ml ?? "");
      if (col.startsWith("comment:")) return csvEscape(r.comments[col.slice(8)] ?? "");
      return csvEscape(r.weeks[col] || "");
    });
    lines.push(cells.join(";"));
  }
  return lines.join("\r\n");
}

export interface ProductionCsvEntry {
  article: string;
  weeks: Record<string, number>;
  comments: Record<string, string>;
}

/** Импорт производства (порт importProductionCsv): по шапке — ключи недель и
 * comment:месяц; group/characteristic/volume_ml игнорируются. Пишется только
 * legacy-уровень недель (затеняется канальными значениями — паритет с оригиналом). */
export function parseProductionCsv(text: string): ProductionCsvEntry[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  if (!lines.length) return [];
  const header = parseCsvLine(lines[0]!);
  const entries: ProductionCsvEntry[] = [];
  for (const line of lines.slice(1)) {
    if (!line.trim()) continue;
    const cols = parseCsvLine(line);
    const article = (cols[0] ?? "").trim();
    if (!article) continue;
    const entry: ProductionCsvEntry = { article, weeks: {}, comments: {} };
    header.forEach((key, i) => {
      if (i < 2) return;
      const value = cols[i] ?? "";
      if (key.startsWith("comment:")) {
        entry.comments[key.slice(8)] = value.trim();
      } else if (/^\d{4}-W\d+$/.test(key)) {
        entry.weeks[key] = csvNum(value);
      }
    });
    entries.push(entry);
  }
  return entries;
}

/** «Списочный» экспорт производства (порт exportProductionPlan): строка на
 * неделю×канал, с диапазоном недели и отметкой «Произведено». */
export function buildProductionListCsv(
  rows: {
    article: string;
    name: string;
    channel_label: string;
    qty: number;
    week_label: string;
    week_range: string;
    produced: boolean;
  }[],
): string {
  const lines = ["Артикул;Наименование;Канал продаж;Количество;Неделя;Диапазон недели;Произведено"];
  for (const r of rows) {
    lines.push(
      [
        csvEscape(r.article),
        csvEscape(r.name),
        csvEscape(r.channel_label),
        csvEscape(r.qty),
        csvEscape(r.week_label),
        csvEscape(r.week_range),
        csvEscape(r.produced ? "Да" : ""),
      ].join(";"),
    );
  }
  return lines.join("\r\n");
}

/** Порт parseTags (стр. 6577): нормализация тегов «#Гель, #Крем» при вводе. */
export function formatTags(value: string): string {
  const tags = value
    .replace(/\r?\n/g, ",")
    .split(/[#,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => `#${t}`);
  return tags.join(", ");
}
